import { cloneDeep } from 'lodash'

import { CATEGORY_TYPE_KVG } from '@/config/constants'
import { DateTimeHelper } from '@/helpers/DateTime'
import store from '@/store'
import { PersonUtil } from '@/utils/Person'

import { ADULT, MINOR, SENIOR } from '@/config/constants'

export const PersonHelper = {
  ...PersonUtil,

  getAgeBracketFromPerson(personId, seniorAge = 65) {
    // eslint-disable-next-line sonarjs/no-duplicate-string
    const person = store.getters['basket/getPerson'](personId)

    if (person.age < 18) {
      return MINOR
    } else if (person.age >= seniorAge) {
      return SENIOR
    } else {
      return ADULT
    }
  },

  getPersonsWithTotal() {
    const persons = cloneDeep(store.getters['basket/getPersons'])

    // eslint-disable-next-line sonarjs/no-duplicate-string
    return persons.map(person => {
      // eslint-disable-next-line sonarjs/no-duplicate-string
      person.products = store.getters['basket/getSelectedProductsOfPerson'](person.personId)

      person.total = person.products.reduce((acc, value) => {
        acc += value.price.price
        return acc
      }, 0)

      return person
    })
  },

  getFirstName(personId, t) {
    const index = store.state.basket.persons.findIndex(p => p.personId === personId)
    // eslint-disable-next-line sonarjs/no-duplicate-string
    const person = store.getters['basket/getPerson'](personId)
    return person.personData.firstName ?? t('person.person', { number: index + 1 })
  },

  getSignees() {
    // eslint-disable-next-line sonarjs/no-duplicate-string
    const persons = store.getters['basket/getPersons']
    const { contractOwner } = store.state.basket
    if (!persons) return []

    // Head should always be the last signee
    const ownerIndex = persons.findIndex(person => person.personId === contractOwner.personId)
    if (ownerIndex > -1) {
      persons.push(persons.splice(ownerIndex, 1)[0])
    }
    return persons
  },

  getSelectedCategoriesMap(persons) {
    return persons.reduce((result, p) => {
      result[p.personId] = {
        isKVGOnly: this.isKVGOnly(p.personId),
        isVVGOnly: this.isVVGOnly(p.personId),
      }
      return result
    }, {})
  },

  // TODO: Replace with store/product.getSelectedProductsByCategory
  getSelectedProductsGroupedByCategory(personId) {
    const categories = store.getters['basket/getCategories'](personId)
    const selectedProducts = store.getters['basket/getSelectedProductsOfPerson'](personId).reduce((acc, value) => {
      acc[value.id] = value
      return acc
    }, {})

    const data = {}

    const createItem = categoryId => {
      if (!data[categoryId]) data[categoryId] = []
    }

    Object.keys(selectedProducts).forEach(productId => {
      let categoryId = store.getters['products/getCategoryIdFromProduct'](productId)

      if (categoryId) {
        createItem(categoryId)

        data[categoryId].push(selectedProducts[productId])
      } else {
        categoryId = Object.keys(data).find(c => data[c].find(p => p?.upgrades?.includes(productId))) ?? 'HOSP'

        createItem(categoryId)

        data[categoryId].push(selectedProducts[productId])
      }
    })

    Object.keys(data).forEach(categoryId => {
      data[categoryId] = data[categoryId].sort((a, b) => (a.order < b.order ? 1 : -1))
    })

    return Object.keys(data)
      .map(categoryId => {
        const products = data[categoryId]

        return {
          id: categoryId,
          products,
        }
      })
      .sort((a, b) => {
        const aCategory = categories.find(c => c.id === a.id)?.order
        const bCategory = categories.find(c => c.id === b.id)?.order

        return aCategory >= bCategory ? 1 : -1
      })
  },

  hasProducts(personId) {
    return store.getters['basket/getSelectedProductsOfPerson'](personId).length > 0
  },

  hasKVG(personId) {
    const products = this.getSelectedProductsGroupedByCategory(personId)
    return products.some(p => p.id === CATEGORY_TYPE_KVG)
  },

  isKVGOnly(personId) {
    const products = this.getSelectedProductsGroupedByCategory(personId)
    return products.length === 1 && products[0].id === CATEGORY_TYPE_KVG
  },

  isUnborn(personId) {
    const person = store.getters['basket/getPerson'](personId)
    return DateTimeHelper.getAge(person.personData.dateOfBirth) < 0
  },

  isVVGOnly(personId) {
    const products = this.getSelectedProductsGroupedByCategory(personId)
    return products.length > 0 && !products.find(p => p.id === CATEGORY_TYPE_KVG)
  },
}
